import { AppConfig } from '@mkp/shared/util-app-config';
import { selectRouterState } from '@mkp/shared/util-router';
import { selectRelevantProductCode } from '@mkp/credit/feature-credit-status/state';
import {
  APPRENTICESHIP_PRODUCT_CODES,
  isProductCode,
  JOBUP_PRODUCT_CODES,
  JS24_PRODUCT_CODES,
  Product,
  PRODUCT_OFFERING_GROUP_MAP,
  ProductCode,
  ProductPlatformGroup,
  SlotCode,
} from '@mkp/shared/models';
import { getUrlWithoutParameters } from '@mkp/shared/util-format';
import {
  getPaidProductsOrderedByPrice,
  getSwitchedPlatformFrom,
  isPaid,
  sortProducts,
} from '@mkp/shared/util-product';
import { createFetchStateSelectors } from '@mkp/shared/util-state';
import { UserDisplayType } from '@mkp/user/models';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromProductOptions from './product-options.reducer';
import { selectUserDisplayType } from '@mkp/user/state-logged-in/selectors';

const state = createFeatureSelector<fromProductOptions.State>(fromProductOptions.featureKey);

export const { selectLoaded: selectProductOptionsLoaded, selectLoading } =
  createFetchStateSelectors(state);

export const selectProducts = createSelector(state, (state) => state?.products);

export const selectProductsDiscount = createSelector(state, (state) => state?.productsDiscount);

export const selectActiveTab = createSelector(state, (state) => state.activeTab);

export const selectPaidJobsProductsOrderedByPrice = createSelector(
  selectProducts,
  getPaidProductsOrderedByPrice(ProductPlatformGroup.Jobs)
);

export const selectPaidJs24ProductsOrderedByPrice = createSelector(
  selectProducts,
  getPaidProductsOrderedByPrice(ProductPlatformGroup.JobScout24)
);

export const selectPaidJobUpProductsOrderedByPrice = createSelector(
  selectProducts,
  getPaidProductsOrderedByPrice(ProductPlatformGroup.JobUp)
);

export const selectSelectedPlatformGroup = createSelector(
  state,
  (state) => state.selectedPlatformGroup
);

export const selectRemovedCmsProductCodes = createSelector(
  state,
  (state) => state.removedCmsProductCodes
);

export const selectActivePlatformGroup = (vacancyId: string) =>
  createSelector(
    selectRelevantProductCode(vacancyId),
    selectSelectedPlatformGroup,
    selectUserDisplayType,
    (
      productCode,
      previouslySelectedPlatformGroup,
      userDisplayType // look for a previously selected platformGroup (in the same session)
    ) =>
      // otherwise, if there is a productCode: use the same platformGroup as the productCode
      // otherwise look for a UserDisplayType from the API
      previouslySelectedPlatformGroup ??
      (productCode
        ? getActivePlatformGroupFromProductCode(productCode)
        : getActivePlatformGroupFromUserDisplayType(userDisplayType))
  );

export const selectPreferredPlatformGroupByDisplayType = () =>
  createSelector(selectUserDisplayType, (userDisplayType) =>
    getActivePlatformGroupFromUserDisplayType(userDisplayType)
  );

export const selectUpgrade = createSelector(state, (state) => state.upgrade);

export const selectFilteredProducts = (vacancyId: string, isForActivePlatformGroup = true) =>
  createSelector(
    selectProducts,
    selectActivePlatformGroup(vacancyId),
    (products, activePlatformGroup) => {
      if (!products || !activePlatformGroup) {
        return [];
      }

      const platformGroup = isForActivePlatformGroup
        ? activePlatformGroup
        : getSwitchedPlatformFrom(activePlatformGroup);
      return products?.filter(({ code }) =>
        PRODUCT_OFFERING_GROUP_MAP[platformGroup]?.includes(code)
      );
    }
  );

export const selectDisplayedProducts = (vacancyId: string, isForActivePlatformGroup = true) =>
  createSelector(
    selectProducts,
    selectFilteredProducts(vacancyId, isForActivePlatformGroup),
    selectUpgrade,
    selectProductsDiscount,
    (availableProducts, products, upgrade, productsDiscount) => {
      if (upgrade?.canUpgrade && availableProducts?.length) {
        const upgradeProductPrice =
          availableProducts?.find(({ code }) => code === upgrade.productCode)?.basePrice ?? 0;

        if (upgradeProductPrice >= 0) {
          return products.filter((product) => product.basePrice > upgradeProductPrice);
        }
      }

      // Discount Price for CMS
      if (productsDiscount && products) {
        products = updatePrices(products, productsDiscount);
      }

      return products.sort(sortProducts);
    }
  );

export const selectUrlWithoutParameters = createSelector(selectRouterState, (state) =>
  getUrlWithoutParameters(state?.state?.url)
);

export const selectPaidDisplayedProducts = (vacancyId: string, isForActivePlatformGroup = true) =>
  createSelector(selectDisplayedProducts(vacancyId, isForActivePlatformGroup), (products) => {
    return products.filter((product) => isPaid(product)).reverse();
  });

export const selectFreeDisplayedProduct = (vacancyId: string, isForActivePlatformGroup = true) =>
  createSelector(selectDisplayedProducts(vacancyId, isForActivePlatformGroup), (products) => {
    if (!products) {
      return null;
    }

    return products.find((product) => !isPaid(product)) ?? null;
  });

export const selectApprenticeshipProducts = createSelector(selectProducts, (products) => {
  if (!products) {
    return [];
  }
  return products.filter(({ code }) => APPRENTICESHIP_PRODUCT_CODES.includes(code));
});

export const selectIsLoginPage = createSelector(selectUrlWithoutParameters, (url) =>
  url.includes(`/${AppConfig.routes.login}`)
);

export const selectIsLandingPage = createSelector(
  selectUrlWithoutParameters,
  (url) => url === `/${AppConfig.routes.home}`
);

const getActivePlatformGroupFromProductCode = (
  productCode: ProductCode | SlotCode
): ProductPlatformGroup => {
  if (isProductCode(productCode)) {
    if (isJS24(productCode)) {
      return ProductPlatformGroup.JobScout24;
    }
    if (isJobUp(productCode)) {
      return ProductPlatformGroup.JobUp;
    }
  }
  return ProductPlatformGroup.Jobs; // default fallback ; should not happen
};

const isJS24 = (code: ProductCode): boolean => JS24_PRODUCT_CODES.includes(code);
const isJobUp = (code: ProductCode): boolean => JOBUP_PRODUCT_CODES.includes(code);

const getActivePlatformGroupFromUserDisplayType = (userDisplayType?: UserDisplayType) => {
  switch (userDisplayType) {
    case UserDisplayType.CustomerJobScout24:
      return ProductPlatformGroup.JobScout24;
    case UserDisplayType.CustomerJobUp:
      return ProductPlatformGroup.JobUp;
    case UserDisplayType.CustomerJobcloud:
    case UserDisplayType.CustomerJobs:
    default:
      return ProductPlatformGroup.Jobs;
  }
};

const updatePrices = (array: Product[], prices: Record<string, number>) => {
  return array.map((item) => {
    if (prices[item.code] !== undefined) {
      return {
        ...item,
        cmsDiscountPrice: {
          currency: item.currency,
          current: prices[item.code],
        },
      };
    }
    return item;
  });
};
